import { UserLicensesQuery } from "@/graphql/queries/UserLicenses";

import { useQueryWrapper } from "@/hooks/useQueryWrapper/useQueryWrapper";

import { useCall } from "@/hooks/useCall/useCall";

import { Banner, BannerHeading, BannerIcon, Box } from "@aircall/tractor";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";
import { LICENSE_NAME } from "../LicenseGuard/LicenseGuard.constant";
import { LoadingView } from "../LoadingView";

/**
 * This layout component is used to check if at least one of the participants has an AI license.
 * If no, we show a banner with a message to the user.
 * If yes, we allow the user to access the evaluation subroute.
 */
export function RequireAgentLicense() {
  const { t } = useTranslation();
  const { call } = useCall();
  const agentId1 = call?.from?.agentId;
  const agentId2 = call?.to?.agentId;

  const { data: data1, loading: loading1 } = useQueryWrapper(
    UserLicensesQuery,
    {
      variables: { userId: agentId1! },
      skip: !agentId1,
    }
  );

  const { data: data2, loading: loading2 } = useQueryWrapper(
    UserLicensesQuery,
    {
      variables: { userId: agentId2! },
      skip: !agentId2,
      onError: (error) => {
        console.error(error);
      },
    }
  );

  if (loading1 || loading2) {
    return <LoadingView className="mt-0 h-full" />;
  }

  const hasAircallAILicense = [data1, data2]
    .flatMap((data) => data?.userLicenses?.licenses ?? [])
    .some((license) =>
      [LICENSE_NAME.AIRCALL_AI, LICENSE_NAME.AI_ASSIST_PRO].some(
        (name) => license.name === name && license.active
      )
    );

  if (!hasAircallAILicense) {
    return (
      <Box p="s" data-test="no-ai-license-from-agent">
        <Banner variant="info" inline>
          <BannerIcon />
          <BannerHeading>{t("evaluation.no_ai_license_banner")}</BannerHeading>
        </Banner>
      </Box>
    );
  }

  return <Outlet />;
}
